import type { MouseEvent } from "react";
import { useState } from "react";
import {
  BellRing,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  FileText,
  Inbox,
  Megaphone,
  ReceiptText,
  RefreshCw,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Notification } from "../types/notification";

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  onMarkAsRead: (notificationIds: string[]) => void;
  onDeleteNotifications: (notificationIds: string[]) => Promise<boolean>;
  onClearAll: () => Promise<boolean>;
  onRefetch: () => void;
}

interface NotificationGroup {
  label: string;
  notifications: Notification[];
}

interface NotificationVisual {
  icon: LucideIcon;
  iconClassName: string;
  wrapperClassName: string;
  eyebrow: string;
}

function getStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDayDifference(dateString: string): number {
  const today = getStartOfDay(new Date());
  const target = getStartOfDay(new Date(dateString));
  return Math.round((today.getTime() - target.getTime()) / 86400000);
}

function getGroupLabel(dateString: string): string {
  const diffDays = getDayDifference(dateString);

  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return `${diffDays} days ago`;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function getNotificationVisual(type: string): NotificationVisual {
  switch (type) {
    case "promotion_created":
      return {
        icon: Megaphone,
        eyebrow: "Promotion",
        iconClassName: "text-purple-600",
        wrapperClassName: "bg-purple-100",
      };
    case "promotion_opted_in":
      return {
        icon: CheckCircle2,
        eyebrow: "Opt-in",
        iconClassName: "text-emerald-600",
        wrapperClassName: "bg-emerald-100",
      };
    case "promotion_opted_out":
      return {
        icon: CircleAlert,
        eyebrow: "Opt-out",
        iconClassName: "text-gray-600",
        wrapperClassName: "bg-gray-100",
      };
    case "dispute_received":
    case "dispute_escalated":
      return {
        icon: ShieldAlert,
        eyebrow: "Dispute",
        iconClassName: "text-purple-600",
        wrapperClassName: "bg-purple-100",
      };
    case "dispute_resolved":
      return {
        icon: CheckCircle2,
        eyebrow: "Resolved",
        iconClassName: "text-emerald-600",
        wrapperClassName: "bg-emerald-100",
      };
    case "redemption_submitted":
    case "redemption_adjusted":
      return {
        icon: ReceiptText,
        eyebrow: "Redemption",
        iconClassName: "text-blue-600",
        wrapperClassName: "bg-blue-100",
      };
    case "invoice_summary":
      return {
        icon: FileText,
        eyebrow: "Invoice",
        iconClassName: "text-blue-600",
        wrapperClassName: "bg-blue-100",
      };
    case "points_reversal_approved_venue":
      return {
        icon: CheckCircle2,
        eyebrow: "Reversal",
        iconClassName: "text-emerald-600",
        wrapperClassName: "bg-emerald-100",
      };
    case "points_reversal_rejected_venue":
      return {
        icon: CircleAlert,
        eyebrow: "Reversal",
        iconClassName: "text-amber-600",
        wrapperClassName: "bg-amber-100",
      };
    case "points_reversal_failed_venue":
      return {
        icon: ShieldAlert,
        eyebrow: "Reversal",
        iconClassName: "text-red-600",
        wrapperClassName: "bg-red-100",
      };
    default:
      return {
        icon: BellRing,
        eyebrow: "Update",
        iconClassName: "text-blue-600",
        wrapperClassName: "bg-blue-50",
      };
  }
}

function getNotificationDestination(notification: Notification): string | null {
  switch (notification.type) {
    case "promotion_created":
    case "promotion_opted_in":
    case "promotion_opted_out":
      return "/landlord-promotions";
    case "dispute_received":
    case "dispute_escalated":
    case "dispute_resolved":
      return "/disputes";
    case "redemption_submitted":
      return "/scanner";
    case "redemption_adjusted":
      return notification.entity_id
        ? `/redemptions/${notification.entity_id}/verify`
        : "/redemptions";
    case "invoice_summary":
      return "/invoices";
    case "points_reversal_approved_venue":
    case "points_reversal_rejected_venue":
    case "points_reversal_failed_venue":
      return "/points/reversals";
    default:
      return null;
  }
}

function groupNotificationsByDate(
  notifications: Notification[]
): NotificationGroup[] {
  const groups = new Map<string, Notification[]>();

  for (const notification of notifications) {
    const label = getGroupLabel(notification.created_at);
    const existing = groups.get(label);

    if (existing) {
      existing.push(notification);
      continue;
    }

    groups.set(label, [notification]);
  }

  return Array.from(groups, ([label, groupedNotifications]) => ({
    label,
    notifications: groupedNotifications,
  }));
}

export default function NotificationCenter({
  isOpen,
  onClose,
  notifications,
  loading,
  error,
  onMarkAsRead,
  onDeleteNotifications,
  onClearAll,
  onRefetch,
}: NotificationCenterProps) {
  const navigate = useNavigate();
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [isClearingAll, setIsClearingAll] = useState(false);

  if (!isOpen) return null;

  const unreadCount = notifications.filter(
    (notification) => !notification.read
  ).length;
  const notificationGroups = groupNotificationsByDate(notifications);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      onMarkAsRead([notification.id]);
    }

    const destination = getNotificationDestination(notification);
    if (destination) {
      navigate(destination);
      onClose();
    }
  };

  const handleDeleteNotification = async (
    event: MouseEvent<HTMLButtonElement>,
    notificationId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (isClearingAll || deletingIds.includes(notificationId)) {
      return;
    }

    setDeletingIds((currentIds) => [...currentIds, notificationId]);
    const deleted = await onDeleteNotifications([notificationId]);
    setDeletingIds((currentIds) =>
      currentIds.filter((id) => id !== notificationId)
    );

    if (!deleted) {
      onRefetch();
    }
  };

  const handleClearAll = async () => {
    if (isClearingAll || notifications.length === 0) {
      return;
    }

    setIsClearingAll(true);
    const cleared = await onClearAll();
    setIsClearingAll(false);

    if (!cleared) {
      onRefetch();
    }
  };

  return (
    <div
      className="fixed inset-x-3 bottom-3 top-[calc(env(safe-area-inset-top)+4.5rem)] z-50 sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-[calc(100%+0.75rem)] sm:w-[min(24rem,calc(100vw-2rem))]"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="absolute -top-2 right-6 hidden h-4 w-4 rotate-45 border-l border-t border-gray-200 bg-white sm:block" />

      <div className="flex h-full flex-col overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-[0_24px_70px_-28px_rgba(15,23,42,0.45)] sm:h-auto sm:rounded-[28px]">
        <div className="border-b border-blue-200 bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-4 sm:px-5">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-white shadow-sm">
              <BellRing className="h-5 w-5" />
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-white sm:text-base">
                Notifications
              </h2>
              <p className="mt-1 text-xs text-blue-100 sm:text-sm">
                {unreadCount} unread
                <span className="px-1.5 text-blue-200">•</span>
                {notifications.length} total
              </p>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleClearAll}
                disabled={notifications.length === 0 || isClearingAll}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/15 px-2.5 py-2 text-[11px] font-semibold text-white transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-3 sm:text-xs"
              >
                {isClearingAll ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
                <span className="sm:hidden">Clear</span>
                <span className="hidden sm:inline">Clear all</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
                aria-label="Close notifications"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50 sm:max-h-[min(32rem,calc(100vh-7rem))]">
          {loading ? (
            <div className="space-y-3 p-3">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-28 animate-pulse rounded-[24px] border border-gray-200 bg-white sm:rounded-3xl"
                />
              ))}
            </div>
          ) : error ? (
            <div className="p-4">
              <div className="rounded-[24px] border border-gray-200 bg-white p-5 text-center shadow-sm sm:rounded-3xl">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
                  <CircleAlert className="h-5 w-5" />
                </div>
                <p className="mt-4 text-sm font-semibold text-gray-900">
                  {error}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  We could not load the latest notifications right now.
                </p>
                <button
                  type="button"
                  onClick={onRefetch}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try again
                </button>
              </div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4">
              <div className="rounded-[24px] border border-dashed border-gray-200 bg-white px-6 py-12 text-center shadow-sm sm:rounded-3xl">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
                  <Inbox className="h-6 w-6" />
                </div>
                <p className="mt-4 text-base font-semibold text-gray-900">
                  All caught up
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  New updates from redemptions, disputes, and promotions will
                  show up here.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-2">
              {notificationGroups.map((group) => (
                <section key={group.label} className="pb-3">
                  <div className="px-3 pb-2 pt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                      {group.label}
                    </p>
                  </div>

                  <div className="space-y-2 px-1">
                    {group.notifications.map((notification) => {
                      const visual = getNotificationVisual(notification.type);
                      const Icon = visual.icon;
                      const isDeleting = deletingIds.includes(notification.id);
                      const destination =
                        getNotificationDestination(notification);

                      return (
                        <div key={notification.id} className="group relative">
                          <button
                            type="button"
                            onClick={() =>
                              handleNotificationClick(notification)
                            }
                            className={`w-full rounded-[24px] border px-4 py-3 pr-12 text-left transition-all sm:rounded-3xl sm:pr-14 ${
                              notification.read
                                ? "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                                : "border-blue-200 bg-[linear-gradient(180deg,_#ffffff_0%,_#eff6ff_100%)] shadow-[0_16px_40px_-28px_rgba(59,130,246,0.45)] hover:border-blue-300"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${visual.wrapperClassName}`}
                              >
                                <Icon
                                  className={`h-5 w-5 ${visual.iconClassName}`}
                                />
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                                      {visual.eyebrow}
                                    </p>
                                    <p className="mt-1 break-words text-sm font-semibold text-gray-900">
                                      {notification.title}
                                    </p>
                                  </div>

                                  {!notification.read && (
                                    <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500" />
                                  )}
                                </div>

                                <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-5 text-gray-600">
                                  {notification.body}
                                </p>

                                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                                  <span>
                                    {formatRelativeTime(
                                      notification.created_at
                                    )}
                                  </span>

                                  {destination && (
                                    <>
                                      <span aria-hidden="true">•</span>
                                      <span className="inline-flex items-center gap-1 font-medium text-blue-600">
                                        Open
                                        <ChevronRight className="h-3.5 w-3.5" />
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={(event) =>
                              handleDeleteNotification(event, notification.id)
                            }
                            disabled={isDeleting || isClearingAll}
                            className="absolute right-2.5 top-2.5 rounded-full border border-gray-200 bg-white p-2 text-gray-400 opacity-100 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-100 sm:right-3 sm:top-3 sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
                            aria-label={`Delete notification: ${notification.title}`}
                          >
                            {isDeleting ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
